/**
 * Utility functions for question processing and HATEOAS link management
 */

/**
 * Ensures a question has the minimal required HATEOAS links structure,
 * specifically the _links.answers.href property to fetch answer options
 * and the _links.details.href property to fetch question details (including lab data).
 * Also adds examId to the question for answer submission.
 * Preserves all existing HATEOAS links to maintain full API functionality.
 * 
 * @param {Object} question - The question object to process
 * @param {string|number} examId - The exam ID to associate with the question
 * @returns {Object} - The question with ensured HATEOAS links and examId
 */
export const ensureMinimalAnswersLinkOnQuestion = (question, examId = null) => {
  if (!question) return question;
  const questionId = question.id || question.questionId; // question.id is primary
  if (!questionId) {
    console.warn('[questionUtils] Question missing ID:', question);
    return question;
  }

  const newQuestion = { ...question }; // Work on a copy

  // Add examId if provided and not already present
  if (examId && !newQuestion.examId) {
    newQuestion.examId = examId;
  }

  // Preserve existing _links object or create new one
  if (!newQuestion._links) {
    newQuestion._links = {};
  } else {
    // Make a deep copy of existing _links to preserve all HATEOAS links including 'details'
    newQuestion._links = { ...newQuestion._links };
  }

  // Ensure the 'answers' link specifically, as per API docs
  if (!newQuestion._links.answers || typeof newQuestion._links.answers !== 'object' || !newQuestion._links.answers.href) {
    const defaultAnswersHref = `/api/question/${questionId}/answers`;
    //console.warn(`[questionUtils] ensureMinimalAnswersLink: Missing or invalid answers link for question ${questionId}. Adding/overwriting with default: ${defaultAnswersHref}`);
    newQuestion._links.answers = {
      href: defaultAnswersHref,
      method: 'GET',
      rel: 'answers'
    };
  }

  // Ensure the 'details' link if not present (needed for lab data)
  if (!newQuestion._links.details || typeof newQuestion._links.details !== 'object' || !newQuestion._links.details.href) {
    const defaultDetailsHref = `/api/question/${questionId}/details`;
    //console.log(`[questionUtils] ensureMinimalAnswersLink: Adding details link for question ${questionId}: ${defaultDetailsHref}`);
    newQuestion._links.details = {
      href: defaultDetailsHref,
      method: 'GET',
      rel: 'details'
    };
  }

  // Add submission endpoint if examId is available
  if (examId && !newQuestion._links.save) {
    const saveHref = `/api/exam/${examId}/question/${questionId}`;
    //console.log(`[questionUtils] Adding save endpoint for question ${questionId}: ${saveHref}`);
    newQuestion._links.save = {
      href: saveHref,
      method: 'POST',
      rel: 'save'
    };
  }

  // Also add the 'save' link for answer submission fallback
  if (examId && questionId && !newQuestion._links.submit) {
    const submitHref = `/api/exam/${examId}/question/${questionId}`;
    newQuestion._links.submit = {
      href: submitHref,
      method: 'POST',
      rel: 'submit'
    };
  }

  return newQuestion;
};

/**
 * Process an array of questions to ensure they all have the required HATEOAS links and examId
 * @param {Array} questions - Array of question objects
 * @param {string|number} examId - The exam ID to associate with all questions
 * @returns {Array} - Array of processed questions
 */
export const processQuestionsForExam = (questions, examId) => {
  if (!Array.isArray(questions)) {
    //console.warn('[questionUtils] Expected questions array, got:', typeof questions);
    return [];
  }

  return questions.map(question => ensureMinimalAnswersLinkOnQuestion(question, examId));
};
